import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, ArrowLeft, Copy, Mail, Play, Plus, Search, Video } from 'lucide-react';
import { useApiClient } from '../lib/apiClient';
import { TelemedVisitStatus } from '../types/telemed';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../i18n/LanguageContext';
import { useAuth0 } from '@auth0/auth0-react';
import { mapPatientFromApi, PatientRow } from '../features/patients/domain';
import InitialAvatar from '../components/InitialAvatar';

type Appointment = {
  id: string;
  patient: string;
  patientId: string;
  provider: string;
  providerId: string;
  providerAvatarUrl?: string | null;
  start: string;
  end: string;
  appointmentType: { name: string; color?: string; isTelemed?: boolean };
  isTelemed?: boolean;
  visitStatus?: TelemedVisitStatus | null;
  joinUrl?: string | null;
  hostToken?: string | null;
  guestToken?: string | null;
  roomName?: string | null;
};

type ProviderOption = {
  id: string;
  name: string;
  avatarUrl?: string | null;
};

type PatientOption = {
  id: string;
  name: string;
  mrn?: string | null;
  phone?: string | null;
  dob?: string | null;
  avatarUrl?: string | null;
};

type AppointmentTypeOption = {
  id: string;
  name: string;
  durationMinutes?: number;
  isTelemed?: boolean;
};

type MePayload = {
  id?: string;
  userId?: string;
  providerId?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
  avatarUrl?: string | null;
  photoUrl?: string | null;
  imageUrl?: string | null;
  profileImageUrl?: string | null;
};

type ProviderPayload = {
  id?: string;
  userId?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  avatarUrl?: string | null;
  photoUrl?: string | null;
  imageUrl?: string | null;
  profileImageUrl?: string | null;
};

function toDateInputValue(date: Date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getInitials(name: string) {
  const initials = name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return initials || 'PT';
}

const statusClasses: Record<string, string> = {
  WAITING: 'bg-sky-50 text-sky-700 border-sky-100',
  IN_SESSION: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  FINISHED: 'bg-slate-100 text-slate-700 border-slate-200',
};

const telemedStatusLabels: Record<string, string> = {
  WAITING: 'Waiting',
  IN_SESSION: 'In session',
  IN_VISIT: 'In session',
  FINISHED: 'Finished',
  ENDED: 'Finished',
};

export default function Telemed() {
  const navigate = useNavigate();
  const { fetchJson } = useApiClient();
  const { user } = useAuth0();
  const { tr } = useI18n();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [telemedAppts, setTelemedAppts] = useState<Appointment[]>([]);
  const [statusFilter, setStatusFilter] = useState<TelemedVisitStatus | 'all'>('all');
  const [query, setQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState(() => toDateInputValue(new Date()));
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);
  const [me, setMe] = useState<MePayload | null>(null);
  const [providerDirectory, setProviderDirectory] = useState<ProviderOption[]>([]);
  const [providerFilterInitialized, setProviderFilterInitialized] = useState(false);
  const [patients, setPatients] = useState<PatientOption[]>([]);
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentTypeOption[]>([]);
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [patient, setPatient] = useState<PatientRow | null>(null);
  const [patientQuery, setPatientQuery] = useState('');
  const [showPatientResults, setShowPatientResults] = useState(false);
  const [showPatientDialog, setShowPatientDialog] = useState(false);
  const [creatingInstantMeeting, setCreatingInstantMeeting] = useState(false);
  const [view, setView] = useState<'table' | 'compose'>('table');

  const myProviderIds = useMemo(() => {
    const candidates = [me?.providerId, me?.userId, me?.id, user?.sub]
      .map((value) => String(value ?? '').trim())
      .filter(Boolean);
    return Array.from(new Set(candidates));
  }, [me, user?.sub]);

  const myName = useMemo(() => {
    const direct = [me?.name, user?.name].find((value) => typeof value === 'string' && value.trim());
    if (direct) return String(direct).trim();
    const joined = `${me?.firstName ?? ''} ${me?.lastName ?? ''}`.trim();
    return joined || 'Me';
  }, [me, user?.name]);

  const myAvatarUrl = useMemo(
    () =>
      me?.avatarUrl ??
      me?.photoUrl ??
      me?.imageUrl ??
      me?.profileImageUrl ??
      (typeof user?.picture === 'string' && user.picture.trim() ? user.picture : null),
    [me, user?.picture],
  );

  const loadTelemed = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [year, month, day] = selectedDate.split('-').map((value) => Number(value));
      const baseDate =
        Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day)
          ? new Date(year, month - 1, day)
          : new Date();
      const start = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), 0, 0, 0, 0);
      const end = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), 23, 59, 59, 999);
      const params = new URLSearchParams({
        start: start.toISOString(),
        end: end.toISOString(),
      });
      const data = await fetchJson<any[]>(`/appointments?${params.toString()}`);
      const mapped: Appointment[] = data
        .map((a) => ({
          id: a.id,
          patient: a.patient?.name ?? ([a.patient?.firstName, a.patient?.lastName].filter(Boolean).join(' ') || 'Patient'),
          patientId: a.patient?.id ?? '',
          provider: a.provider?.name ?? 'Provider',
          providerId: a.provider?.id ?? '',
          providerAvatarUrl:
            a.provider?.avatarUrl ??
            a.provider?.photoUrl ??
            a.provider?.imageUrl ??
            a.provider?.profileImageUrl ??
            null,
          start: a.startTime,
          end: a.endTime,
          appointmentType: {
            name: a.appointmentType?.name ?? 'Telemed Room',
            color: a.appointmentType?.color,
            isTelemed: a.appointmentType?.isTelemed,
          },
          isTelemed: Boolean(a.isTelemed || a.appointmentType?.isTelemed),
          visitStatus: a.visitStatus ?? a.telemed?.visitStatus ?? null,
          joinUrl: a.joinUrl ?? a.telemed?.joinUrl ?? null,
          hostToken: a.hostToken ?? a.telemed?.hostToken ?? null,
          guestToken: a.guestToken ?? a.telemed?.guestToken ?? null,
          roomName: a.roomName ?? a.telemed?.roomName ?? null,
        }))
        .filter((a) => a.isTelemed);
      setTelemedAppts(mapped);
    } catch (err: any) {
      setError(err.message || tr('Failed to load telemed visits'));
    } finally {
      setLoading(false);
    }
  }, [fetchJson, selectedDate, tr]);

  const loadProviderDirectory = useCallback(async () => {
    try {
      const providers = await fetchJson<ProviderPayload[]>('/providers');
      const normalized: ProviderOption[] = (Array.isArray(providers) ? providers : [])
        .map((provider) => ({
          id: String(provider.userId ?? provider.id ?? '').trim(),
          name: `${provider.firstName ?? ''} ${provider.lastName ?? ''}`.trim() || 'Provider',
          avatarUrl: provider.avatarUrl ?? provider.photoUrl ?? provider.imageUrl ?? provider.profileImageUrl ?? null,
        }))
        .filter((provider) => Boolean(provider.id));
      setProviderDirectory(normalized);
    } catch {
      setProviderDirectory([]);
    }
  }, [fetchJson]);

  const loadMe = useCallback(async () => {
    try {
      const profile = await fetchJson<MePayload>('/users/me');
      setMe(profile);
    } catch {
      setMe(null);
    }
  }, [fetchJson]);

  const loadPatients = useCallback(async () => {
    try {
      const response = await fetchJson<{ data: Array<any> }>('/patients?page=1&pageSize=100');
      const normalized = (Array.isArray(response?.data) ? response.data : []).map((entry) => ({
        id: String(entry.id ?? '').trim(),
        name: `${entry.firstName ?? ''} ${entry.lastName ?? ''}`.trim() || entry.mrn || 'Patient',
        mrn: entry.mrn ?? null,
        phone: entry.phone ?? entry.mobilePhone ?? entry.contactPhone ?? null,
        dob: entry.dob ?? entry.dateOfBirth ?? null,
        avatarUrl: entry.avatarUrl ?? entry.photoUrl ?? entry.imageUrl ?? entry.profilePhotoUrl ?? entry.profileImageUrl ?? null,
      }));
      setPatients(normalized.filter((entry) => Boolean(entry.id)));
    } catch {
      setPatients([]);
    }
  }, [fetchJson]);

  const loadAppointmentTypes = useCallback(async () => {
    try {
      const types = await fetchJson<AppointmentTypeOption[]>('/appointment-types');
      setAppointmentTypes(Array.isArray(types) ? types : []);
    } catch {
      setAppointmentTypes([]);
    }
  }, [fetchJson]);

  useEffect(() => {
    loadTelemed();
  }, [loadTelemed]);

  useEffect(() => {
    void loadProviderDirectory();
    void loadMe();
    void loadPatients();
    void loadAppointmentTypes();
  }, [loadAppointmentTypes, loadMe, loadPatients, loadProviderDirectory]);

  useEffect(() => {
    setProviderFilterInitialized(false);
  }, [selectedDate]);

  useEffect(() => {
    if (!selectedPatientId) return;
    let cancelled = false;
    async function loadPatientDetails() {
      try {
        const detail = await fetchJson<any>(`/patients/${selectedPatientId}`);
        if (!cancelled) {
          setPatient(mapPatientFromApi(detail));
          setView('compose');
          setShowPatientDialog(false);
          setShowPatientResults(false);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err.message || tr('Failed to load patient details.'));
        }
      }
    }
    void loadPatientDetails();
    return () => {
      cancelled = true;
    };
  }, [fetchJson, selectedPatientId, tr]);

  const providerOptions = useMemo(() => {
    const map = new Map<string, ProviderOption>();
    telemedAppts.forEach((appointment) => {
      if (!appointment.providerId) return;
      const existing = map.get(appointment.providerId);
      map.set(appointment.providerId, {
        id: appointment.providerId,
        name: appointment.provider || existing?.name || 'Provider',
        avatarUrl: appointment.providerAvatarUrl ?? existing?.avatarUrl ?? null,
      });
    });
    providerDirectory.forEach((provider) => {
      if (!provider.id) return;
      const existing = map.get(provider.id);
      map.set(provider.id, {
        id: provider.id,
        name: existing?.name || provider.name || 'Provider',
        avatarUrl: existing?.avatarUrl ?? provider.avatarUrl ?? null,
      });
    });
    if (map.size === 0 && myProviderIds.length > 0) {
      const fallbackId = myProviderIds[0];
      map.set(fallbackId, {
        id: fallbackId,
        name: myName || 'Provider',
        avatarUrl: myAvatarUrl ?? null,
      });
    }
    const list = Array.from(map.values());
    list.sort((a, b) => {
      const aMine = myProviderIds.includes(a.id) ? 0 : 1;
      const bMine = myProviderIds.includes(b.id) ? 0 : 1;
      if (aMine !== bMine) return aMine - bMine;
      return a.name.localeCompare(b.name);
    });
    return list;
  }, [myAvatarUrl, myName, myProviderIds, providerDirectory, telemedAppts]);

  const myProviderMatches = useMemo(() => {
    const myNameNormalized = myName.trim().toLowerCase();
    const ids = providerOptions
      .filter((provider) => {
        if (myProviderIds.includes(provider.id)) return true;
        return Boolean(myNameNormalized) && provider.name.trim().toLowerCase() === myNameNormalized;
      })
      .map((provider) => provider.id);
    return Array.from(new Set(ids));
  }, [myName, myProviderIds, providerOptions]);

  const otherProviderOptions = useMemo(
    () => providerOptions.filter((provider) => !myProviderMatches.includes(provider.id)),
    [myProviderMatches, providerOptions],
  );

  const defaultProviderId = useMemo(
    () => myProviderMatches[0] || selectedProviders[0] || providerOptions[0]?.id || '',
    [myProviderMatches, providerOptions, selectedProviders],
  );

  const telemedAppointmentType = useMemo(
    () => appointmentTypes.find((type) => type.isTelemed) || null,
    [appointmentTypes],
  );

  const filteredPatients = useMemo(() => {
    const q = patientQuery.trim().toLowerCase();
    if (!q) return patients.slice(0, 12);
    return patients
      .filter((entry) => [entry.name, entry.mrn, entry.phone].map((value) => String(value ?? '').toLowerCase()).join(' ').includes(q))
      .slice(0, 12);
  }, [patientQuery, patients]);

  const selectedPatientMeta = useMemo(() => {
    if (!patient) return { metaLine: '', initials: 'PT' };
    const age = patient.age ? `${patient.age} ${tr('yrs')}` : null;
    const metaLine = [
      patient.mrn ? `MRN ${patient.mrn}` : null,
      age,
      patient.dob ? `${tr('DOB')} ${new Date(patient.dob).toLocaleDateString()}` : null,
      patient.sex || null,
    ]
      .filter(Boolean)
      .join(' | ');
    const initials = `${patient.firstName?.[0] ?? ''}${patient.lastName?.[0] ?? ''}`.trim().toUpperCase() || 'PT';
    return { metaLine, initials };
  }, [patient, tr]);

  useEffect(() => {
    if (!providerOptions.length) {
      setSelectedProviders([]);
      return;
    }

    setSelectedProviders((prev) => prev.filter((id) => providerOptions.some((provider) => provider.id === id)));

    if (providerFilterInitialized) return;

    if (myProviderMatches.length) {
      setSelectedProviders([myProviderMatches[0]]);
      setProviderFilterInitialized(true);
      return;
    }

    setSelectedProviders([providerOptions[0].id]);
    setProviderFilterInitialized(true);
  }, [myProviderMatches, providerFilterInitialized, providerOptions]);

  const filteredAppts = useMemo(() => {
    const q = query.toLowerCase().trim();
    return telemedAppts.filter((appointment) => {
      const status = appointment.visitStatus || 'WAITING';
      const statusMatch = statusFilter === 'all' || status === statusFilter;
      const providerMatch = !selectedProviders.length || selectedProviders.includes(appointment.providerId);
      const textMatch =
        !q ||
        appointment.patient.toLowerCase().includes(q) ||
        appointment.provider.toLowerCase().includes(q) ||
        appointment.appointmentType.name.toLowerCase().includes(q);
      return statusMatch && providerMatch && textMatch;
    });
  }, [query, selectedProviders, statusFilter, telemedAppts]);

  const quickJoin = (appointment: Appointment, asHost: boolean) => {
    navigate(`/telemedicine/${appointment.id}?role=${asHost ? 'host' : 'guest'}`);
  };

  const sendEmailLink = async (appointment: Appointment) => {
    if (!appointment.joinUrl) {
      setError(tr('No join link available to email.'));
      return;
    }
    try {
      await fetchJson(`/appointments/${appointment.id}/notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ joinUrl: appointment.joinUrl, recipient: appointment.patient }),
      } as any);
    } catch (err: any) {
      setError(err.message || tr('Failed to send email'));
    }
  };

  const copyLink = async (url?: string | null) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // ignore clipboard errors
    }
  };

  const createInstantMeeting = useCallback(async () => {
    if (!selectedPatientId) {
      setError(tr('Select a patient to start an instant meeting.'));
      return;
    }
    if (!defaultProviderId) {
      setError(tr('No provider available for this instant meeting.'));
      return;
    }
    if (!telemedAppointmentType?.id) {
      setError(tr('No telemedicine appointment type is configured.'));
      return;
    }

    try {
      setCreatingInstantMeeting(true);
      setError(null);
      const start = new Date();
      const durationMinutes = Number(telemedAppointmentType.durationMinutes || 30);
      const end = new Date(start.getTime() + durationMinutes * 60_000);
      const created = await fetchJson<{ id: string }>('/appointments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patientId: selectedPatientId,
          providerId: defaultProviderId,
          appointmentTypeId: telemedAppointmentType.id,
          status: 'SCHEDULED',
          startTime: start.toISOString(),
          endTime: end.toISOString(),
          title: 'Instant Telemed',
          isTelemed: true,
        }),
      });
      setPatient(null);
      setSelectedPatientId('');
      setPatientQuery('');
      setShowPatientResults(false);
      setView('table');
      await loadTelemed();
      navigate(`/telemedicine/${created.id}?role=host`);
    } catch (err: any) {
      setError(err.message || tr('Failed to create telemed session'));
    } finally {
      setCreatingInstantMeeting(false);
    }
  }, [defaultProviderId, fetchJson, loadTelemed, navigate, selectedPatientId, telemedAppointmentType, tr]);

  function openPatientDialog() {
    setError(null);
    setPatientQuery('');
    setShowPatientResults(false);
    setShowPatientDialog(true);
  }

  function returnToTable() {
    setView('table');
    setPatient(null);
    setSelectedPatientId('');
    setPatientQuery('');
    setShowPatientDialog(false);
  }

  return (
    <div className="space-y-4">
      <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-lime-700">{tr('Telemedicine')}</p>
            <h1 className="mt-1 text-2xl font-semibold text-ink">{tr('Virtual Waiting Room')}</h1>
            <p className="max-w-3xl text-sm text-slate-600">
              {tr('Recent telemedicine visits, patient intake, and instant room launch in one workspace.')}
            </p>
          </div>
          <button
            type="button"
            onClick={view === 'compose' ? returnToTable : openPatientDialog}
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold ${
              view === 'compose'
                ? 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                : 'bg-lime-400 text-slate-900 hover:bg-lime-300'
            }`}
          >
            {view === 'compose' ? <ArrowLeft size={16} /> : <Plus size={16} />}
            {view === 'compose' ? tr('Cancel Telemed') : tr('Telemed Room')}
          </button>
        </div>
      </div>

      {error ? (
        <div className="flex items-center gap-2 rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          <AlertCircle size={14} /> {error}
        </div>
      ) : null}

      {view === 'table' ? (
        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">{tr('Telemed Visits')}</h2>
              <p className="text-sm text-slate-500">{tr('Join or copy links directly.')}</p>
            </div>
            <button
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              onClick={loadTelemed}
              disabled={loading}
            >
              {tr('Refresh')}
            </button>
          </div>

          <div className="space-y-4 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative w-full md:w-72">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 pl-9 text-sm outline-none focus:border-lime-400 focus:ring-2 focus:ring-lime-100"
                  placeholder={tr('Search patient, provider, type...')}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                />
              </div>
              <input
                type="date"
                className="rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm"
                value={selectedDate}
                onChange={(event) => setSelectedDate(event.target.value)}
                aria-label={tr('Appointment date')}
              />
              <select
                className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value as any)}
              >
                <option value="all">{tr('All statuses')}</option>
                <option value="WAITING">{tr('Waiting')}</option>
                <option value="IN_SESSION">{tr('In session')}</option>
                <option value="FINISHED">{tr('Finished')}</option>
              </select>
              <div className="ml-auto flex items-center gap-2 flex-wrap">
                <span className="text-xs text-slate-500">{tr('Doctors')}</span>
                {(myProviderMatches.length > 0 || Boolean(myAvatarUrl) || Boolean(myName)) && (
                  <button
                    type="button"
                    className={`h-8 w-8 overflow-hidden rounded-full border-2 text-[10px] font-semibold transition ${
                      selectedProviders.some((providerId) => myProviderMatches.includes(providerId))
                        ? 'border-ink ring-2 ring-ink/20'
                        : 'border-slate-200 hover:border-slate-400'
                    }`}
                    disabled={myProviderMatches.length === 0}
                    onClick={() =>
                      setSelectedProviders((prev) => {
                        const currentlyActive = myProviderMatches.some((providerId) => prev.includes(providerId));
                        if (currentlyActive) {
                          const next = prev.filter((providerId) => !myProviderMatches.includes(providerId));
                          return next.length ? next : prev;
                        }
                        const merged = [...prev];
                        myProviderMatches.forEach((providerId) => {
                          if (!merged.includes(providerId)) merged.push(providerId);
                        });
                        return merged;
                      })
                    }
                    title={`${tr('My schedule')}: ${myName}`}
                    aria-label={`${tr('My schedule')}: ${myName}`}
                  >
                    <InitialAvatar name={myName} src={myAvatarUrl} alt={myName} className="h-full w-full border-0" />
                  </button>
                )}
                {otherProviderOptions.map((provider) => {
                  const active = selectedProviders.includes(provider.id);
                  return (
                    <button
                      key={provider.id}
                      type="button"
                      className={`h-8 w-8 overflow-hidden rounded-full border-2 text-[10px] font-semibold transition ${
                        active ? 'border-ink ring-2 ring-ink/20' : 'border-slate-200 hover:border-slate-400'
                      }`}
                      title={provider.name}
                      aria-label={provider.name}
                      onClick={() =>
                        setSelectedProviders((prev) => {
                          const isActive = prev.includes(provider.id);
                          if (isActive) {
                            const next = prev.filter((id) => id !== provider.id);
                            return next.length ? next : prev;
                          }
                          return [...prev, provider.id];
                        })
                      }
                    >
                      <InitialAvatar name={provider.name} src={provider.avatarUrl} alt={provider.name} className="h-full w-full border-0" />
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-3 font-medium">{tr('Patient')}</th>
                    <th className="px-4 py-3 font-medium">{tr('Provider')}</th>
                    <th className="px-4 py-3 font-medium">{tr('Visit type')}</th>
                    <th className="px-4 py-3 font-medium">{tr('Time')}</th>
                    <th className="px-4 py-3 font-medium">{tr('Status')}</th>
                    <th className="px-4 py-3 font-medium text-right">{tr('Actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-sm text-slate-500">{tr('Loading telemed visits.')}</td>
                    </tr>
                  ) : null}
                  {!loading && filteredAppts.map((appointment) => {
                    const start = new Date(appointment.start).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
                    const end = new Date(appointment.end).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
                    const status = appointment.visitStatus || 'WAITING';
                    return (
                      <tr key={appointment.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 align-top">
                          <div className="font-medium text-slate-800">{appointment.patient}</div>
                        </td>
                        <td className="px-4 py-3 align-top text-slate-600">{appointment.provider}</td>
                        <td className="px-4 py-3 align-top text-slate-600">{tr(appointment.appointmentType.name)}</td>
                        <td className="px-4 py-3 align-top text-slate-600">{start} - {end}</td>
                        <td className="px-4 py-3 align-top">
                          <span className={`inline-flex items-center rounded-full border px-2 py-1 text-xs font-medium ${statusClasses[status] || statusClasses.WAITING}`}>
                            {tr(telemedStatusLabels[status] || status)}
                          </span>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                              onClick={() => quickJoin(appointment, true)}
                              title={tr('Join as provider (host)')}
                            >
                              <Play size={12} /> {tr('Join')}
                            </button>
                            <button
                              className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                              onClick={() => copyLink(appointment.joinUrl)}
                              disabled={!appointment.joinUrl}
                              title={tr('Copy patient link')}
                            >
                              <Copy size={12} /> {tr('Link')}
                            </button>
                            <button
                              className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                              onClick={() => sendEmailLink(appointment)}
                              disabled={!appointment.joinUrl}
                              title={tr('Send email invite')}
                            >
                              <Mail size={12} /> {tr('Email')}
                            </button>
                            {appointment.patientId ? (
                              <button
                                className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                                onClick={() => setSelectedPatientId(appointment.patientId)}
                              >
                                <Video size={12} /> {tr('Open')}
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {!loading && filteredAppts.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-sm text-slate-500">{tr('No telemed visits found.')}</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      ) : null}

      {view === 'compose' && patient ? (
        <div className="space-y-4">
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-start gap-4">
              <InitialAvatar name={`${patient.firstName} ${patient.lastName}`} src={patient.avatarUrl} alt={`${patient.firstName} ${patient.lastName}`} className="h-14 w-14 text-sm" />
              <div className="space-y-1">
                <p className="text-lg font-semibold text-slate-800">{patient.firstName} {patient.lastName}</p>
                {selectedPatientMeta.metaLine ? <p className="text-sm font-medium text-slate-600">{selectedPatientMeta.metaLine}</p> : null}
                <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600">
                  {patient.phone ? <span>{patient.phone}</span> : null}
                  {patient.email ? <span>{patient.email}</span> : null}
                  {patient.address ? <span>{patient.address}</span> : null}
                </div>
              </div>
            </div>
            <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-lime-50 px-3 py-1.5 text-sm font-medium text-lime-800">
              <Video size={14} />
              {tr('Telemed room for selected patient below.')}
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">{tr('Instant meeting')}</p>
                <h2 className="text-xl font-semibold text-slate-900">{tr('Launch Virtual Room')}</h2>
                <p className="mt-1 text-sm text-slate-500">{tr('Create a telemedicine appointment instantly and open the provider room.')}</p>
              </div>
              <button
                type="button"
                className="rounded-xl bg-lime-400 px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-lime-300 disabled:opacity-60"
                onClick={() => void createInstantMeeting()}
                disabled={creatingInstantMeeting || !selectedPatientId || !defaultProviderId || !telemedAppointmentType}
              >
                {creatingInstantMeeting ? tr('Creating...') : tr('Start instant meeting')}
              </button>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs uppercase tracking-wide text-slate-500">{tr('Provider')}</div>
                <div className="mt-2 flex items-center gap-3">
                  <InitialAvatar
                    name={providerOptions.find((provider) => provider.id === defaultProviderId)?.name || myName}
                    src={providerOptions.find((provider) => provider.id === defaultProviderId)?.avatarUrl}
                    alt={providerOptions.find((provider) => provider.id === defaultProviderId)?.name || myName}
                    className="h-10 w-10"
                  />
                  <div className="min-w-0">
                    <div className="font-medium text-slate-800">{providerOptions.find((provider) => provider.id === defaultProviderId)?.name || myName}</div>
                    <div className="text-xs text-slate-500">{tr('Auto-selected from your current schedule')}</div>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs uppercase tracking-wide text-slate-500">{tr('Appointment Type')}</div>
                <div className="mt-2 font-medium text-slate-800">
                  {telemedAppointmentType?.name ? tr(telemedAppointmentType.name) : tr('No telemedicine appointment type configured')}
                </div>
                <div className="text-xs text-slate-500">
                  {telemedAppointmentType?.durationMinutes
                    ? `${telemedAppointmentType.durationMinutes} ${tr('minutes')}`
                    : tr('Uses the configured default duration')}
                </div>
              </div>
            </div>
          </section>
        </div>
      ) : null}

      {showPatientDialog ? (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-950/40 p-4" onClick={() => setShowPatientDialog(false)}>
          <div
            className="mt-10 w-full max-w-2xl rounded-3xl border border-slate-200 bg-white p-5 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-lime-700">{tr('Select a Patient')}</p>
                <h2 className="mt-1 text-xl font-semibold text-slate-900">{tr('Start telemed')}</h2>
                <p className="text-sm text-slate-500">{tr('Search for an existing patient to start this telemedicine meeting.')}</p>
              </div>
              <button
                type="button"
                onClick={() => setShowPatientDialog(false)}
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                {tr('Close')}
              </button>
            </div>

            <div className="relative mt-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                autoFocus
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 pl-9 text-sm outline-none focus:border-lime-400 focus:ring-2 focus:ring-lime-100"
                placeholder={tr('Search by name, MRN, or phone...')}
                value={patientQuery}
                onChange={(event) => {
                  const value = event.target.value;
                  setPatientQuery(value);
                  setShowPatientResults(true);
                }}
                onFocus={() => setShowPatientResults(true)}
                onBlur={() => window.setTimeout(() => setShowPatientResults(false), 120)}
                disabled={creatingInstantMeeting}
              />

              {showPatientResults ? (
                <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-[320px] overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-lg">
                  {filteredPatients.length > 0 ? (
                    filteredPatients.map((entry) => {
                      const rightMeta = [entry.mrn ? `MRN ${entry.mrn}` : null, entry.phone || null].filter(Boolean).join(' • ');
                      return (
                        <button
                          key={entry.id}
                          type="button"
                          className="flex w-full items-center gap-3 border-b border-slate-100 px-3 py-3 text-left transition-colors last:border-0 hover:bg-slate-50"
                          onMouseDown={(event) => {
                            event.preventDefault();
                            setSelectedPatientId(entry.id);
                            setPatientQuery(entry.name);
                            setShowPatientResults(false);
                          }}
                        >
                          <InitialAvatar name={entry.name} src={entry.avatarUrl} alt={entry.name} className="h-10 w-10" />
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium text-slate-800">{entry.name}</div>
                            <div className="truncate text-xs text-slate-500">
                              {[entry.dob ? `${tr('DOB')} ${new Date(entry.dob).toLocaleDateString()}` : null, rightMeta].filter(Boolean).join(' • ')}
                            </div>
                          </div>
                        </button>
                      );
                    })
                  ) : (
                    <div className="p-3 text-sm text-slate-500">{tr('No patients found.')}</div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

    </div>
  );
}
